import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAstrologers, updateAstrologer, updateAstrologerApproval, createAstrologer, resolveMediaUrl } from '../../api';
import { PageHeader, StatusBadge, Table, Pagination, SearchInput, EmptyState, TableSkeleton } from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import Modal from '../../components/ui/Modal';
import ImageUploadField from '../../components/ui/ImageUploadField';
import { formatCurrency } from '../../utils/format';
import {
  Star, CheckCircle, XCircle, Eye, RefreshCw, Edit, Filter, X, Users,
  Activity, Clock, Ban, ShieldCheck, PhoneCall, MessageSquare, Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';

type CreatedCredentials = {
  loginId: string;
  password: string;
  email?: string | null;
  phone?: string;
  name?: string;
  shareMessage?: string;
};

const SPECIALITIES = ['love', 'career', 'marriage', 'health', 'finance', 'kundli', 'numerology', 'tarot', 'vastu', 'palmistry'];
const LANGUAGES = ['en', 'hi', 'ta', 'te', 'ml', 'kn', 'mr', 'bn'];

const ASTRO_COLS = [
  { key: '_id', label: 'ID' }, { key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' }, { key: 'experience', label: 'Experience (yrs)' },
  { key: 'rating', label: 'Rating' }, { key: 'reviewCount', label: 'Reviews' },
  { key: 'totalConsultations', label: 'Consultations' }, { key: 'chatPrice', label: 'Chat Rate (paise)' },
  { key: 'callPrice', label: 'Audio Rate (paise)' }, { key: 'availabilityStatus', label: 'Status' },
  { key: 'approvalStatus', label: 'Approval' }, { key: 'createdAt', label: 'Joined' },
];

type AstroRow = { _id: string; name: string; phone: string; email?: string; profileImage?: string; bio?: string; specialities: string[]; languages: string[]; experience: number; chatPrice: number; callPrice: number; videoPrice: number; rating: number; reviewCount: number; totalConsultations: number; availabilityStatus: string; approvalStatus: string; isActive: boolean; isVerified: boolean; earnings: number; createdAt: string; };

export default function AstrologersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [approvalFilter, setApprovalFilter] = useState(searchParams.get('approval') || '');
  const [specialityFilter, setSpecialityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('active') === '1' ? 'active' : searchParams.get('active') === '0' ? 'inactive' : ''
  );
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setApprovalFilter(searchParams.get('approval') || '');
    const a = searchParams.get('active');
    setStatusFilter(a === '1' ? 'active' : a === '0' ? 'inactive' : '');
    setPage(1);
  }, [searchParams]);

  const [editAstro, setEditAstro] = useState<AstroRow | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<CreatedCredentials | null>(null);
  const [approveModal, setApproveModal] = useState<{ id: string; name: string; action: string } | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [editForm, setEditForm] = useState<any>({});
  const [createForm, setCreateForm] = useState({
    name: '', phone: '', email: '', bio: '', experience: 1,
    specialities: [] as string[], languages: ['hi'],
    chatPrice: 30, callPrice: 50, videoPrice: 70,
    chatMinMinutes: 5, callMinMinutes: 5, videoMinMinutes: 10,
    profileImage: '',
    password: '',
  });

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const emptyCreateForm = () => ({
    name: '', phone: '', email: '', bio: '', experience: 1, specialities: [] as string[], languages: ['hi'],
    chatPrice: 30, callPrice: 50, videoPrice: 70,
    chatMinMinutes: 5, callMinMinutes: 5, videoMinMinutes: 10,
    profileImage: '',
    password: '',
  });

  const { data, isLoading } = useQuery<{ data: AstroRow[]; pagination?: any }>({
    queryKey: ['admin-astrologers', page, search, approvalFilter, specialityFilter, statusFilter],
    queryFn: async () => {
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (approvalFilter) params.approvalStatus = approvalFilter;
      if (statusFilter) params.isActive = statusFilter === 'active';
      const r = await getAstrologers(params);
      return r.data;
    },
    placeholderData: (prev) => prev,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => updateAstrologer(id, data),
    onSuccess: () => { toast.success('Astrologer updated'); qc.invalidateQueries({ queryKey: ['admin-astrologers'] }); setEditAstro(null); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Update failed'),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => createAstrologer(data),
    onSuccess: (res) => {
      const creds = res.data?.data?.credentials as CreatedCredentials | undefined;
      toast.success(res.data?.message || 'Astrologer created');
      qc.invalidateQueries({ queryKey: ['admin-astrologers'] });
      setCreateModal(false);
      setCreateForm(emptyCreateForm());
      if (creds?.loginId && creds?.password) {
        setCreatedCreds(creds);
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Create failed'),
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, approvalStatus, note }: any) => updateAstrologerApproval(id, { approvalStatus, approvalNote: note }),
    onSuccess: (_, v) => {
      toast.success(`Astrologer ${v.approvalStatus}`);
      qc.invalidateQueries({ queryKey: ['admin-astrologers'] });
      setApproveModal(null); setApprovalNote('');
    },
  });

  const openEdit = (a: AstroRow) => {
    setEditAstro(a);
    setEditForm({
      name: a.name, email: a.email || '', bio: a.bio || '', experience: a.experience,
      specialities: a.specialities || [], languages: a.languages || [],
      chatPrice: a.chatPrice / 100, callPrice: a.callPrice / 100, videoPrice: a.videoPrice / 100,
      chatMinMinutes: (a as any).chatMinMinutes || 5,
      callMinMinutes: (a as any).callMinMinutes || 5,
      videoMinMinutes: (a as any).videoMinMinutes || 10,
      profileImage: a.profileImage || '',
      isActive: a.isActive,
    });
  };

  const astrologers = data?.data || [];
  const pagination = data?.pagination;

  const stats = [
    { label: 'Total', value: pagination?.total?.toLocaleString() ?? '—', icon: <Users size={14} /> },
    { label: 'Online', value: astrologers.filter((a) => a.availabilityStatus === 'online').length, icon: <Activity size={14} />, color: 'text-green-600' },
    { label: 'Pending', value: astrologers.filter((a) => a.approvalStatus === 'pending').length, icon: <Clock size={14} />, color: 'text-yellow-600' },
    { label: 'Approved', value: astrologers.filter((a) => a.approvalStatus === 'approved').length, icon: <ShieldCheck size={14} />, color: 'text-blue-600' },
    { label: 'Suspended', value: astrologers.filter((a) => a.approvalStatus === 'suspended').length, icon: <Ban size={14} />, color: 'text-red-600' },
    { label: 'On Page', value: astrologers.length, icon: <Star size={14} /> },
  ];

  const clearFilters = () => { setApprovalFilter(''); setSpecialityFilter(''); setStatusFilter(''); setSearch(''); setPage(1); };
  const hasFilters = approvalFilter || specialityFilter || statusFilter || search;

  const toggleMulti = (list: string[], val: string, setter: (v: string[]) => void) => {
    setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  };

  return (
    <div className="p-6">
      <PageHeader title="Astrologers" subtitle="Manage astrologer profiles, approvals & pricing">
        <ExportButton data={astrologers} filename="astrologers" columns={ASTRO_COLS} />
        <button onClick={() => setCreateModal(true)} className="btn-primary text-sm">+ Add Astrologer</button>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-astrologers'] })} className="btn-secondary btn-sm"><RefreshCw size={14} /></button>
      </PageHeader>

      <OverviewBar items={stats} loading={isLoading} />

      {/* Filters */}
      <div className="card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px]">
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search name, phone…" />
          </div>
          {/* Approval quick tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {[['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['suspended', 'Suspended']].map(([v, l]) => (
              <button key={v} onClick={() => { setApprovalFilter(v); setPage(1); }} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${approvalFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary btn-sm ${showFilters ? 'bg-gray-100' : ''}`}>
            <Filter size={14} /> More {hasFilters && <span className="ml-1 bg-primary-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">!</span>}
          </button>
          {hasFilters && <button onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1"><X size={12} />Clear</button>}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Availability</label>
              <select className="input" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="busy">Busy</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Speciality</label>
              <select className="input" value={specialityFilter} onChange={(e) => { setSpecialityFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {SPECIALITIES.map((s) => <option key={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={10} cols={8} /></div>
        ) : astrologers.length === 0 ? (
          <EmptyState message="No astrologers found" icon={<Star size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">#</th>
                <th className="table-th">Astrologer</th>
                <th className="table-th">Specialities</th>
                <th className="table-th">Rating</th>
                <th className="table-th">Chat/Audio/Video</th>
                <th className="table-th">Consultations</th>
                <th className="table-th">Availability</th>
                <th className="table-th">Approval</th>
                <th className="table-th text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {astrologers.map((a, idx) => (
                <tr key={a._id} className="table-row">
                  <td className="table-td text-xs text-gray-400">{(page - 1) * 20 + idx + 1}</td>
                  <td className="table-td">
                    <button
                      type="button"
                      onClick={() => navigate(`/astrologers/${a._id}`)}
                      className="flex items-center gap-2.5 text-left hover:opacity-80"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden">
                        {a.profileImage ? (
                          <img src={resolveMediaUrl(a.profileImage)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          a.name?.[0]?.toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-xs">{a.name}</p>
                        <p className="text-gray-400 text-xs font-mono">{a.phone}</p>
                      </div>
                    </button>
                  </td>
                  <td className="table-td">
                    <div className="flex flex-wrap gap-1">
                      {a.specialities?.slice(0, 2).map((s) => <span key={s} className="badge badge-orange capitalize">{s}</span>)}
                      {(a.specialities?.length || 0) > 2 && <span className="badge badge-gray">+{a.specialities.length - 2}</span>}
                    </div>
                  </td>
                  <td className="table-td">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-yellow-400 fill-yellow-400" />
                      <span className="font-semibold text-xs">{a.rating?.toFixed(1)}</span>
                      <span className="text-gray-400 text-xs">({a.reviewCount})</span>
                    </div>
                  </td>
                  <td className="table-td text-xs">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1"><MessageSquare size={10} className="text-blue-400" />{formatCurrency(a.chatPrice)}/m</div>
                      <div className="flex items-center gap-1"><PhoneCall size={10} className="text-green-400" />{formatCurrency(a.callPrice)}/m</div>
                    </div>
                  </td>
                  <td className="table-td text-xs font-semibold">{a.totalConsultations?.toLocaleString()}</td>
                  <td className="table-td"><StatusBadge status={a.availabilityStatus} /></td>
                  <td className="table-td"><StatusBadge status={a.approvalStatus} /></td>
                  <td className="table-td">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Open Astrologer Details" onClick={() => navigate(`/astrologers/${a._id}`)} className="p-1.5 rounded hover:bg-blue-50 text-blue-500"><Eye size={14} /></button>
                      <button title="Edit" onClick={() => openEdit(a)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Edit size={14} /></button>
                      {a.approvalStatus === 'pending' && (
                        <>
                          <button title="Approve" onClick={() => setApproveModal({ id: a._id, name: a.name, action: 'approved' })} className="p-1.5 rounded hover:bg-green-50 text-green-500"><CheckCircle size={14} /></button>
                          <button title="Reject" onClick={() => setApproveModal({ id: a._id, name: a.name, action: 'rejected' })} className="p-1.5 rounded hover:bg-red-50 text-red-500"><XCircle size={14} /></button>
                        </>
                      )}
                      {a.approvalStatus === 'approved' && (
                        <button title="Suspend" onClick={() => setApproveModal({ id: a._id, name: a.name, action: 'suspended' })} className="p-1.5 rounded hover:bg-yellow-50 text-yellow-500"><Ban size={14} /></button>
                      )}
                      {(a.approvalStatus === 'rejected' || a.approvalStatus === 'suspended') && (
                        <button title="Re-Approve" onClick={() => setApproveModal({ id: a._id, name: a.name, action: 'approved' })} className="p-1.5 rounded hover:bg-green-50 text-green-500"><CheckCircle size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t border-gray-100">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={20} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* ── Edit Astrologer Modal ────────────────────────────────────────────── */}
      <Modal open={!!editAstro} onClose={() => setEditAstro(null)} title={`Edit — ${editAstro?.name}`} size="lg">
        <form onSubmit={(e) => {
          e.preventDefault();
          const d = {
            ...editForm,
            chatPrice: Math.round(Number(editForm.chatPrice) * 100),
            callPrice: Math.round(Number(editForm.callPrice) * 100),
            videoPrice: Math.round(Number(editForm.videoPrice) * 100),
            chatMinMinutes: Number(editForm.chatMinMinutes) || 5,
            callMinMinutes: Number(editForm.callMinMinutes) || 5,
            videoMinMinutes: Number(editForm.videoMinMinutes) || 10,
          };
          updateMutation.mutate({ id: editAstro!._id, data: d });
        }} className="space-y-4">
          <ImageUploadField
            label="Profile Image"
            value={editForm.profileImage || ''}
            onChange={(url) => setEditForm({ ...editForm, profileImage: url })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Name</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input className="input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Experience (years)</label><input type="number" className="input" value={editForm.experience} onChange={(e) => setEditForm({ ...editForm, experience: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Chat ₹/min</label><input type="number" step="0.5" className="input" value={editForm.chatPrice} onChange={(e) => setEditForm({ ...editForm, chatPrice: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Audio ₹/min</label><input type="number" step="0.5" className="input" value={editForm.callPrice} onChange={(e) => setEditForm({ ...editForm, callPrice: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Video ₹/min</label><input type="number" step="0.5" className="input" value={editForm.videoPrice} onChange={(e) => setEditForm({ ...editForm, videoPrice: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Chat min mins</label><input type="number" min="1" className="input" value={editForm.chatMinMinutes} onChange={(e) => setEditForm({ ...editForm, chatMinMinutes: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Audio min mins</label><input type="number" min="1" className="input" value={editForm.callMinMinutes} onChange={(e) => setEditForm({ ...editForm, callMinMinutes: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Video min mins</label><input type="number" min="1" className="input" value={editForm.videoMinMinutes} onChange={(e) => setEditForm({ ...editForm, videoMinMinutes: e.target.value })} /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Bio</label><textarea rows={3} className="input resize-none" value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} /></div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Specialities</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALITIES.map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={editForm.specialities?.includes(s)} onChange={() => toggleMulti(editForm.specialities || [], s, (v) => setEditForm({ ...editForm, specialities: v }))} />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Languages</label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => (
                <label key={l} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={editForm.languages?.includes(l)} onChange={() => toggleMulti(editForm.languages || [], l, (v) => setEditForm({ ...editForm, languages: v }))} />
                  <span className="uppercase">{l}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} /> Active</label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => setEditAstro(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={updateMutation.isPending} className="btn-primary flex-1 justify-center">{updateMutation.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>

      {/* ── Create Astrologer Modal ──────────────────────────────────────────── */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Add New Astrologer" size="lg">
        <form onSubmit={(e) => {
          e.preventDefault();
          const payload: any = {
            ...createForm,
            chatPrice: createForm.chatPrice,
            callPrice: createForm.callPrice,
            videoPrice: createForm.videoPrice,
            approvalStatus: 'approved',
          };
          if (!String(createForm.password || '').trim()) delete payload.password;
          createMutation.mutate(payload);
        }} className="space-y-4">
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            A unique <strong>Astrologer ID</strong> (from name) and <strong>password</strong> are generated automatically.
            Credentials are shown once after create — copy and share them with the astrologer.
          </p>
          <ImageUploadField
            label="Profile Image"
            value={createForm.profileImage}
            onChange={(url) => setCreateForm({ ...createForm, profileImage: url })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label><input required className="input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Phone (10 digits) *</label><input required className="input" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="9876543210" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input type="email" className="input" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Experience (years)</label><input type="number" min="0" className="input" value={createForm.experience} onChange={(e) => setCreateForm({ ...createForm, experience: Number(e.target.value) })} /></div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Password (optional)</label>
              <input
                className="input"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Leave blank to auto-generate a unique password"
              />
            </div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Chat ₹/min</label><input type="number" step="0.5" min="1" className="input" value={createForm.chatPrice} onChange={(e) => setCreateForm({ ...createForm, chatPrice: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Audio ₹/min</label><input type="number" step="0.5" min="1" className="input" value={createForm.callPrice} onChange={(e) => setCreateForm({ ...createForm, callPrice: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Video ₹/min</label><input type="number" step="0.5" min="1" className="input" value={createForm.videoPrice} onChange={(e) => setCreateForm({ ...createForm, videoPrice: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Chat min (mins)</label><input type="number" min="1" className="input" value={createForm.chatMinMinutes} onChange={(e) => setCreateForm({ ...createForm, chatMinMinutes: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Audio min (mins)</label><input type="number" min="1" className="input" value={createForm.callMinMinutes} onChange={(e) => setCreateForm({ ...createForm, callMinMinutes: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Video min (mins)</label><input type="number" min="1" className="input" value={createForm.videoMinMinutes} onChange={(e) => setCreateForm({ ...createForm, videoMinMinutes: Number(e.target.value) })} /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Bio</label><textarea rows={2} className="input resize-none" value={createForm.bio} onChange={(e) => setCreateForm({ ...createForm, bio: e.target.value })} /></div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Specialities</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALITIES.map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={createForm.specialities.includes(s)} onChange={() => toggleMulti(createForm.specialities, s, (v) => setCreateForm({ ...createForm, specialities: v }))} />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => setCreateModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={createMutation.isPending} className="btn-primary flex-1 justify-center">
              {createMutation.isPending ? 'Creating…' : 'Create Astrologer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Credentials after create ─────────────────────────────────────────── */}
      <Modal open={!!createdCreds} onClose={() => setCreatedCreds(null)} title="Share login details" size="md">
        {createdCreds && (
          <div className="space-y-4">
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Password is shown only once. Copy and share with the astrologer (SMS/email not connected yet).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Name</div>
                <div className="font-medium">{createdCreds.name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Phone</div>
                <div className="font-medium">{createdCreds.phone || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 sm:col-span-2">
                <div className="text-xs text-gray-400">Email</div>
                <div className="font-medium break-all">{createdCreds.email || '—'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Astrologer ID</div>
                <div className="font-mono font-semibold">{createdCreds.loginId}</div>
              </div>
              <button type="button" className="btn-secondary" onClick={() => copyText(createdCreds.loginId)}><Copy size={14} /></button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Password</div>
                <div className="font-mono font-semibold">{createdCreds.password}</div>
              </div>
              <button type="button" className="btn-secondary" onClick={() => copyText(createdCreds.password)}><Copy size={14} /></button>
            </div>
            {createdCreds.shareMessage && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs text-gray-500">Share message</div>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => copyText(createdCreds.shareMessage!)}>
                    <Copy size={12} className="inline mr-1" /> Copy message
                  </button>
                </div>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap font-sans text-gray-700">
                  {createdCreds.shareMessage}
                </pre>
              </div>
            )}
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => setCreatedCreds(null)}>Done</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Approval Confirm Modal ───────────────────────────────────────────── */}
      <Modal open={!!approveModal} onClose={() => { setApproveModal(null); setApprovalNote(''); }} title={`${approveModal?.action === 'approved' ? 'Approve' : approveModal?.action === 'rejected' ? 'Reject' : 'Suspend'} Astrologer`} size="sm">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">You are about to <strong>{approveModal?.action}</strong> <strong>{approveModal?.name}</strong>.</p>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label><textarea rows={2} className="input resize-none" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} placeholder="Reason or note for this decision…" /></div>
          <div className="flex gap-2">
            <button onClick={() => { setApproveModal(null); setApprovalNote(''); }} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button
              onClick={() => approveMutation.mutate({ id: approveModal!.id, approvalStatus: approveModal!.action, note: approvalNote })}
              disabled={approveMutation.isPending}
              className={`flex-1 justify-center btn ${approveModal?.action === 'approved' ? 'btn-primary' : 'btn-danger'}`}
            >
              {approveMutation.isPending ? '…' : `Confirm ${approveModal?.action}`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
